import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import DimensionsControl from './DimensionsControl';
import DragDropUpload from './DragDropUpload';
import ExportPanel from './ExportPanel';

const EditorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #ddd;
  margin: 0.5rem;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const SvgContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: #f0f0f0;
  background-image: linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
`;

const SvgWrapper = styled.div`
  position: absolute;
  transform-origin: 0 0;
  transform: translate(${props => props.panX}px, ${props => props.panY}px) scale(${props => props.zoom / 100});
  transition: transform 0.1s ease;
  cursor: ${props => props.isPanning ? 'grabbing' : 'grab'};
`;

const ZoomControls = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
  gap: 5px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px;
  border-radius: 4px;
  z-index: 5;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  
  &:hover {
    background-color: #555;
  }
`;

const ZoomText = styled.span`
  padding: 0 8px;
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: center;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  z-index: 5;
  
  &:hover {
    background-color: #555;
  }
`;


const ExportContainer = styled.div`
  padding: 10px;
  background-color: #f8f8f8;
  border-top: 1px solid #ddd;
`;

const SvgEditor = ({ svgCode, setSvgCode }) => {
  const [zoom, setZoom] = useState(100);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [startPanX, setStartPanX] = useState(0);
  const [startPanY, setStartPanY] = useState(0);
  const [showDimensions, setShowDimensions] = useState(false);
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setZoom(prev => {
      const newZoom = Math.min(prev + 2, 400);
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 2, 2);
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoom(100);
    setPanX(0);
    setPanY(0);
  };
  
  const handleFitToView = () => {
    if (!containerRef.current || !svgRef.current) return;
    
    try {
      // Reset pan position
      setPanX(0);
      setPanY(0);
      
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      
      // Get SVG dimensions
      const svgRect = svgRef.current.getBoundingClientRect();
      const svgWidth = svgRect.width / (zoom / 100);
      const svgHeight = svgRect.height / (zoom / 100);
      
      // Calculate scale to fit
      const scaleX = containerWidth / svgWidth;
      const scaleY = containerHeight / svgHeight;
      const scale = Math.min(scaleX, scaleY) * 0.9; // 90% of the container
      
      // Set zoom level
      setZoom(Math.floor(scale * 100));
      
      // Center the SVG
      const centerX = (containerWidth - (svgWidth * scale)) / 2;
      const centerY = (containerHeight - (svgHeight * scale)) / 2;
      
      setPanX(centerX);
      setPanY(centerY);
    } catch (error) {
      console.error('Error fitting SVG to view:', error);
    }
  };
  
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left mouse button
      setIsPanning(true);
      setStartPanX(e.clientX - panX);
      setStartPanY(e.clientY - panY);
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };
  
  const handleMouseMove = (e) => {
    if (isPanning) {
      const newPanX = e.clientX - startPanX;
      const newPanY = e.clientY - startPanY;
      setPanX(newPanX);
      setPanY(newPanY);
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    }
  };
  
  const handleMouseUp = () => {
    setIsPanning(false);
  };
  
  const handleMouseLeave = () => {
    setIsPanning(false);
  };
  
  const handleWheel = (e) => {
    e.preventDefault();
    
    // Get mouse position relative to container
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate position relative to the current pan and zoom
    const svgX = (mouseX - panX) / (zoom / 100);
    const svgY = (mouseY - panY) / (zoom / 100);
    
    // Determine zoom direction and calculate new zoom
    const delta = -Math.sign(e.deltaY);
    const zoomFactor = delta > 0 ? 1.1 : 0.9;
    const newZoom = Math.max(20, Math.min(400, zoom * zoomFactor));
    
    // Calculate new pan position to zoom toward mouse position
    const newPanX = mouseX - svgX * (newZoom / 100);
    const newPanY = mouseY - svgY * (newZoom / 100);
    
    setZoom(newZoom);
    setPanX(newPanX);
    setPanY(newPanY);
  };
  
  // Auto-fit SVG when it changes
  useEffect(() => {
    if (svgCode) {
      // Small delay to ensure the SVG is rendered
      const timer = setTimeout(() => {
        handleFitToView();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [svgCode]);
  
  // Add event listeners for mouse wheel
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [zoom, panX, panY]);

  return (
    <EditorContainer>
      <SvgContainer 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >

    <ZoomControls>
        <ZoomButton onClick={handleZoomOut} title="Zoom Out">-</ZoomButton>
        <ZoomText>{Math.round(zoom)}%</ZoomText>
        <ZoomButton onClick={handleZoomIn} title="Zoom In">+</ZoomButton>
        <ZoomButton onClick={handleResetZoom} title="Reset Zoom">1:1</ZoomButton>
        <ZoomButton onClick={handleFitToView} title="Fit to View">⤢</ZoomButton>
      </ZoomControls>

        <SvgWrapper 
          zoom={zoom}
          panX={panX}
          panY={panY}
          isPanning={isPanning}
          ref={svgRef}
          dangerouslySetInnerHTML={{ __html: svgCode }} 
        />
      </SvgContainer>
      

      
      <ToggleButton onClick={() => setShowDimensions(!showDimensions)}>
        {showDimensions ? 'Hide Dimensions' : 'Edit Dimensions'}
      </ToggleButton>
      
      {showDimensions && (
        <DimensionsControl svgCode={svgCode} setSvgCode={setSvgCode} />
      )}
      
      <DragDropUpload setSvgCode={setSvgCode} />
      
      {/* <DropHint>
        Drag & drop SVG files here
      </DropHint> */}
      
      <ExportContainer>
        <ExportPanel svgCode={svgCode} />
      </ExportContainer>
    </EditorContainer>
  );
};

export default SvgEditor; 